/**
 * 🎬 WALKTHROUGH PROVIDER CONTEXT
 * Epic 8 Implementation - Progressive Walkthrough System
 * 
 * Complete React context system for managing walkthrough state
 * Supports Epic 7.5 baseline through Epic 16 evolution
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  WalkthroughState, 
  WalkthroughContextType, 
  Walkthrough, 
  WalkthroughPreferences,
  EPIC_WALKTHROUGHS
} from '../types/walkthrough';
import { 
  loadFeatureFlags, 
  saveFeatureFlags,
  isFeatureEnabled,
  enableFeature,
  disableFeature
} from '../utils/featureFlags';

// Walkthrough Actions
type WalkthroughAction =
  | { type: 'START_WALKTHROUGH'; payload: Walkthrough }
  | { type: 'STOP_WALKTHROUGH' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREVIOUS_STEP' }
  | { type: 'SKIP_TO_STEP'; payload: number }
  | { type: 'COMPLETE_WALKTHROUGH' }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<WalkthroughPreferences> }
  | { type: 'SHOW_OVERLAY' }
  | { type: 'HIDE_OVERLAY' }
  | { type: 'SET_COMPLETED_WALKTHROUGHS'; payload: string[] };

// Local Storage Keys
const WALKTHROUGH_STATE_KEY = 'crowsnest-walkthrough-state';
const COMPLETED_WALKTHROUGHS_KEY = 'crowsnest-completed-walkthroughs';
const PREFERENCES_KEY = 'crowsnest-walkthrough-preferences';

// Default preferences
const DEFAULT_PREFERENCES: WalkthroughPreferences = {
  autoAdvance: false,
  showTooltips: true,
  animationSpeed: 'normal',
  skipCompleted: true,
  language: 'en'
};

// Initial state
const initialState: WalkthroughState = {
  activeWalkthrough: null,
  currentStepIndex: 0,
  isPlaying: false,
  isComplete: false,
  completedWalkthroughs: [],
  userPreferences: DEFAULT_PREFERENCES,
  overlayVisible: false
};

// Walkthrough Reducer
function walkthroughReducer(state: WalkthroughState, action: WalkthroughAction): WalkthroughState {
  switch (action.type) {
    case 'START_WALKTHROUGH':
      return {
        ...state,
        activeWalkthrough: action.payload,
        currentStepIndex: 0,
        isPlaying: true,
        isComplete: false,
        overlayVisible: true
      };

    case 'STOP_WALKTHROUGH':
      return {
        ...state,
        activeWalkthrough: null,
        currentStepIndex: 0,
        isPlaying: false,
        isComplete: false,
        overlayVisible: false
      };

    case 'NEXT_STEP':
      if (!state.activeWalkthrough) return state;
      
      const nextIndex = state.currentStepIndex + 1;
      const isCompleting = nextIndex >= state.activeWalkthrough.steps.length;
      
      return {
        ...state,
        currentStepIndex: isCompleting ? state.currentStepIndex : nextIndex,
        isComplete: isCompleting,
        isPlaying: !isCompleting
      };

    case 'PREVIOUS_STEP':
      if (!state.activeWalkthrough) return state;
      
      const prevIndex = Math.max(0, state.currentStepIndex - 1);
      return {
        ...state,
        currentStepIndex: prevIndex,
        isComplete: false,
        isPlaying: true
      };

    case 'SKIP_TO_STEP':
      if (!state.activeWalkthrough) return state;
      
      const targetIndex = Math.max(0, Math.min(action.payload, state.activeWalkthrough.steps.length - 1));
      return {
        ...state,
        currentStepIndex: targetIndex,
        isComplete: false,
        isPlaying: true
      };

    case 'COMPLETE_WALKTHROUGH':
      if (!state.activeWalkthrough) return state;
      
      const newCompletedWalkthroughs = state.completedWalkthroughs.includes(state.activeWalkthrough.id)
        ? state.completedWalkthroughs
        : [...state.completedWalkthroughs, state.activeWalkthrough.id];
      
      return {
        ...state,
        isComplete: true,
        isPlaying: false,
        completedWalkthroughs: newCompletedWalkthroughs,
        overlayVisible: false
      };

    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        userPreferences: { ...state.userPreferences, ...action.payload }
      };

    case 'SHOW_OVERLAY':
      return { ...state, overlayVisible: true };

    case 'HIDE_OVERLAY':
      return { ...state, overlayVisible: false };

    case 'SET_COMPLETED_WALKTHROUGHS':
      return { ...state, completedWalkthroughs: action.payload };

    default:
      return state;
  }
}

// Create Context
const WalkthroughContext = createContext<WalkthroughContextType | null>(null);

// Provider Props
interface WalkthroughProviderProps {
  children: ReactNode;
}

/**
 * WalkthroughProvider - Main context provider for walkthrough system
 */
export function WalkthroughProvider({ children }: WalkthroughProviderProps) {
  const [state, dispatch] = useReducer(walkthroughReducer, initialState);

  // Initialize state from localStorage
  useEffect(() => {
    try {
      // Load completed walkthroughs
      const completedWalkthroughs = localStorage.getItem(COMPLETED_WALKTHROUGHS_KEY);
      if (completedWalkthroughs) {
        dispatch({ 
          type: 'SET_COMPLETED_WALKTHROUGHS', 
          payload: JSON.parse(completedWalkthroughs) 
        });
      }

      // Load preferences
      const preferences = localStorage.getItem(PREFERENCES_KEY);
      if (preferences) {
        dispatch({ 
          type: 'UPDATE_PREFERENCES', 
          payload: JSON.parse(preferences) 
        });
      }
    } catch (error) {
      console.warn('Failed to load walkthrough state from storage:', error);
    }
  }, []);

  // Persist state changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COMPLETED_WALKTHROUGHS_KEY, JSON.stringify(state.completedWalkthroughs));
    } catch (error) {
      console.warn('Failed to save completed walkthroughs:', error);
    }
  }, [state.completedWalkthroughs]);

  useEffect(() => {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(state.userPreferences));
    } catch (error) {
      console.warn('Failed to save walkthrough preferences:', error);
    }
  }, [state.userPreferences]);

  // Context methods
  const startWalkthrough = (walkthroughId: string) => {
    const walkthrough = getWalkthroughById(walkthroughId);
    if (!walkthrough) {
      console.warn(`Walkthrough ${walkthroughId} not found`);
      return;
    }

    // Check if user has access to this walkthrough
    const featureFlags = loadFeatureFlags();
    if (walkthrough.requiredFlags) {
      const hasAccess = walkthrough.requiredFlags.every(flag => 
        isFeatureEnabled(flag as keyof typeof featureFlags, featureFlags)
      );
      
      if (!hasAccess) {
        console.warn(`Access denied to walkthrough ${walkthroughId}. Required features not enabled.`);
        return;
      }
    }

    // Skip if completed and user prefers to skip
    if (state.userPreferences.skipCompleted && isWalkthroughCompleted(walkthroughId)) {
      console.log(`Skipping completed walkthrough: ${walkthroughId}`);
      return;
    }

    dispatch({ type: 'START_WALKTHROUGH', payload: walkthrough });
  };

  const stopWalkthrough = () => {
    dispatch({ type: 'STOP_WALKTHROUGH' });
  };

  const nextStep = () => {
    if (state.activeWalkthrough && state.currentStepIndex >= state.activeWalkthrough.steps.length - 1) {
      completeWalkthrough();
    } else {
      dispatch({ type: 'NEXT_STEP' });
    }
  };

  const previousStep = () => {
    dispatch({ type: 'PREVIOUS_STEP' });
  };

  const skipToStep = (stepIndex: number) => {
    dispatch({ type: 'SKIP_TO_STEP', payload: stepIndex });
  };

  const completeWalkthrough = () => {
    dispatch({ type: 'COMPLETE_WALKTHROUGH' });
  };

  const updatePreferences = (preferences: Partial<WalkthroughPreferences>) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: preferences });
  };

  const showOverlay = () => {
    dispatch({ type: 'SHOW_OVERLAY' });
  };

  const hideOverlay = () => {
    dispatch({ type: 'HIDE_OVERLAY' });
  };

  // Feature flag management
  const featureFlags = loadFeatureFlags();
  
  const isFeatureFlagEnabled = (flag: keyof typeof featureFlags) => {
    return isFeatureEnabled(flag, featureFlags);
  };

  const enableFeatureFlag = (flag: keyof typeof featureFlags) => {
    enableFeature(flag);
  };

  const disableFeatureFlag = (flag: keyof typeof featureFlags) => {
    disableFeature(flag);
  };

  // Walkthrough management
  const getAvailableWalkthroughs = (): Walkthrough[] => {
    const currentFeatureFlags = loadFeatureFlags();
    
    return Object.values(EPIC_WALKTHROUGHS).filter(walkthrough => {
      // Check if user has access based on feature flags
      if (walkthrough.requiredFlags) {
        return walkthrough.requiredFlags.every(flag => 
          isFeatureEnabled(flag as keyof typeof currentFeatureFlags, currentFeatureFlags)
        );
      }
      return true;
    });
  };

  const getWalkthroughById = (id: string): Walkthrough | null => {
    return EPIC_WALKTHROUGHS[id] || null;
  };

  const isWalkthroughCompleted = (id: string): boolean => {
    return state.completedWalkthroughs.includes(id);
  };

  // Context value
  const contextValue: WalkthroughContextType = {
    state,
    startWalkthrough,
    stopWalkthrough,
    nextStep,
    previousStep,
    skipToStep,
    completeWalkthrough,
    updatePreferences,
    featureFlags,
    isFeatureEnabled: isFeatureFlagEnabled,
    enableFeature: enableFeatureFlag,
    disableFeature: disableFeatureFlag,
    getAvailableWalkthroughs,
    getWalkthroughById,
    isWalkthroughCompleted,
    showOverlay,
    hideOverlay
  };

  return (
    <WalkthroughContext.Provider value={contextValue}>
      {children}
    </WalkthroughContext.Provider>
  );
}

/**
 * Custom hook to use walkthrough context
 */
export function useWalkthrough(): WalkthroughContextType {
  const context = useContext(WalkthroughContext);
  
  if (!context) {
    throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  }
  
  return context;
}

/**
 * Custom hook for feature flag access in components
 */
export function useFeatureFlags() {
  const { featureFlags, isFeatureEnabled, enableFeature, disableFeature } = useWalkthrough();
  
  return {
    flags: featureFlags,
    isEnabled: isFeatureEnabled,
    enable: enableFeature,
    disable: disableFeature
  };
}

/**
 * Higher-order component for feature flag protection
 */
export function withFeatureFlag<P extends object>(
  Component: React.ComponentType<P>,
  requiredFlag: string,
  fallback?: React.ComponentType<P>
) {
  return function FeatureFlaggedComponent(props: P) {
    const { isFeatureEnabled } = useWalkthrough();
    
    if (!isFeatureEnabled(requiredFlag as any)) {
      if (fallback) {
        const FallbackComponent = fallback;
        return <FallbackComponent {...props} />;
      }
      return null;
    }
    
    return <Component {...props} />;
  };
}
